import React from 'react'
import { Grid } from '@mui/material'
import { compact, groupBy, isNil, keys, maxBy, mean, round, sumBy, uniqBy } from 'lodash'
import { calculateAverage, searchMaxAnalysis, searchMinAnalysis, searchP90Analysis } from 'utils/AnalyseUtils'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { calculateThresholdsResult, THRESHOLD_COLOR } from 'utils/QualityThresholdUtils'

const Chip = ({
    color = '',
}) => (
    <span
        style={{
            backgroundColor: color,
            color,
            padding: '0px 6px',
            borderRadius: '10px',
            marginRight: '5px',
        }}
    >
        {'0'}
    </span>
)

Chip.propTypes = {
    color: PropTypes.string,
}

const getStatsTaxon = (validHydrobios, sortingType, qualityThresholds, selectedTaxon) => {
    const groupedByStation = groupBy(validHydrobios, 'qualitometer')
    return keys(groupedByStation).map(qualito => {
        const filteredList = !isNil(selectedTaxon) ? groupedByStation[qualito].filter(l => l.taxon === selectedTaxon) : groupedByStation[qualito]
        const groupedByTaxon = groupBy(filteredList, 'taxon')
        const statsByParam = compact(keys(groupedByTaxon).map(taxon => {
            if (sortingType === '1') {
                return searchMaxAnalysis(groupedByTaxon[taxon])
            } else if (sortingType === '2') {
                return searchMinAnalysis(groupedByTaxon[taxon])
            } else if (sortingType === '3') {
                const average = calculateAverage(groupedByTaxon[taxon])
                const hydrobio = groupedByTaxon[taxon][0]
                const averageHydrobio = {
                    result: average,
                    unit: hydrobio?.unit,
                    fraction: hydrobio?.fraction,
                    support: hydrobio?.support,
                    qualitometer: hydrobio?.qualitometer,
                }
                return {
                    ...averageHydrobio,
                    ...calculateThresholdsResult(averageHydrobio, qualityThresholds),
                }
            } else if (sortingType === '4') {
                return searchP90Analysis(groupedByTaxon[taxon])
            }
            return maxBy(groupedByTaxon[taxon], 'operationDate')
        }))
        return maxBy(statsByParam, s => s.index ?? -1) ?? { result: 0, qualitometer: groupedByStation[qualito]?.[0]?.qualitometer }
    })
}

// const getStatsTaxon = (hydrobioList, sortingType, qualityThresholds, selectedTaxon) => {
//     const validHydrobios = filterValid(filterResult(hydrobioList))
//     return getStats(validHydrobios, sortingType, qualityThresholds, selectedTaxon)
// }

const getPresenceMissingTaxon = (selectedTaxon, hydrobioList) => {
    // const validHydrobios = filterValid(filterResult(hydrobioList))
    const groupedHydrobios = groupBy(hydrobioList, 'qualitometer')
    return keys(groupedHydrobios).map(key => {
        const isPresent = groupedHydrobios[key].some(l => l.taxon === selectedTaxon)
        return {
            qualitometer: groupedHydrobios[key][0].qualitometer,
            color: isPresent ? THRESHOLD_COLOR.green : THRESHOLD_COLOR.red,
        }
    })
}

const PresenceMissingLegend = () => (
    <Grid container>
        <Grid size={6}>
            <Chip color='green'/>
            {i18n.present}
        </Grid>
        <Grid size={6}>
            <Chip color='red'/>
            {i18n.absent}
        </Grid>
    </Grid>
)

const getAbundanceColor = abundance => {
    if (abundance > 50) return THRESHOLD_COLOR.blue
    if (abundance > 25) return THRESHOLD_COLOR.green
    if (abundance > 10) return THRESHOLD_COLOR.yellow
    if (abundance > 5) return THRESHOLD_COLOR.orange
    return THRESHOLD_COLOR.red
}

const getAbundanceTaxon = (selectedTaxon, hydrobioList, sortingType) => {
    // const validHydrobios = filterValid(filterResult(hydrobioList))
    const filteredList = hydrobioList.filter(l => l.taxon === selectedTaxon)
    const stats = getStatsTaxon(filteredList, sortingType)
    const groupedHydrobios = groupBy(hydrobioList, 'qualitometer')
    const keyFiltered = keys(groupedHydrobios)

    if (sortingType === '3') { // average
        return keyFiltered.map(key => {
            const stat = stats.find(s => `${s.qualitometer}` === key)
            const groupedOperation = groupBy(groupedHydrobios[key], 'operation')
            const meanByOperation = mean(keys(groupedOperation).map(k => sumBy(groupedOperation[k], 'result')))
            const abundance = meanByOperation && !isNil(stat?.result) ? round(stat.result * 100 / meanByOperation, 3) : 0
            return {
                qualitometer: groupedHydrobios[key][0].qualitometer,
                color: getAbundanceColor(abundance),
            }
        })
    }

    return keyFiltered.map(key => {
        const stat = stats.find(s => `${s.qualitometer}` === key)
        const subList = groupedHydrobios[key].filter(l => l.operation === stat?.operation)
        const sumList = sumBy(subList, 'result')
        const abundance = sumList && !isNil(stat?.result) ? round(stat.result * 100 / sumList, 3) : 0
        return {
            qualitometer: groupedHydrobios[key][0].qualitometer,
            color: getAbundanceColor(abundance),
        }
    })
}

const AbundanceLegend = () => (
    <Grid container>
        <Grid size={6}>
            <Chip color='blue'/>
            {'> 50'}
        </Grid>
        <Grid size={6}>
            <Chip color='green'/>
            {'<= 50 et > 25'}
        </Grid>
        <Grid size={6}>
            <Chip color='yellow'/>
            {'<= 25 et > 10'}
        </Grid>
        <Grid size={6}>
            <Chip color='orange'/>
            {'<= 10 et > 5'}
        </Grid>
        <Grid size={6}>
            <Chip color='red'/>
            {'<= 5'}
        </Grid>
    </Grid>
)

const getFrequencyColor = frequency => {
    if (frequency > 90) return THRESHOLD_COLOR.blue
    if (frequency > 75) return THRESHOLD_COLOR.green
    if (frequency > 50) return THRESHOLD_COLOR.yellow
    if (frequency > 25) return THRESHOLD_COLOR.orange
    return THRESHOLD_COLOR.red
}

const getOccurenceFrequencyTaxon = (selectedTaxon, hydrobioList) => {
    // const validHydrobios = filterValid(filterResult(hydrobioList))
    const groupedHydrobios = groupBy(hydrobioList, 'qualitometer')

    return keys(groupedHydrobios).map(key => {
        const subList = groupedHydrobios[key]
        const nbAllOperation = uniqBy(subList, 'operation').length
        const nbDetected = uniqBy(subList.filter(l => l.taxon === selectedTaxon), 'operation').length
        const frequency = nbAllOperation ? round(nbDetected * 100 / nbAllOperation, 3) : 0
        return {
            qualitometer: groupedHydrobios[key][0].qualitometer,
            color: getFrequencyColor(frequency),
        }
    })
}

const OccurenceFrequencyLegend = () => (
    <Grid container>
        <Grid size={6}>
            <Chip color='blue'/>
            {'> 90'}
        </Grid>
        <Grid size={6}>
            <Chip color='green'/>
            {'<= 90 et > 75'}
        </Grid>
        <Grid size={6}>
            <Chip color='yellow'/>
            {'<= 75 et > 50'}
        </Grid>
        <Grid size={6}>
            <Chip color='orange'/>
            {'<= 50 et > 25'}
        </Grid>
        <Grid size={6}>
            <Chip color='red'/>
            {'<= 25'}
        </Grid>
    </Grid>
)

export {
    getStatsTaxon,
    getPresenceMissingTaxon,
    getAbundanceTaxon,
    getOccurenceFrequencyTaxon,
    PresenceMissingLegend,
    AbundanceLegend,
    OccurenceFrequencyLegend,
}